import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import { auth } from '../firebaseConfig';
import FullScreenButton from './FullScreenButton';
import {
  buildProcurementInvoiceFilename,
  downloadProcurementInvoicePdf,
} from '../utils/downloadProcurementInvoicePdf';
import type { ProcurementInvoicePdfData } from '../utils/procurementInvoicePdf';
import { formatPhoneKoreanInput } from '../utils/phone';
import { todayLocalDate, toDateOnlyString, monthStartLocalDate, daysFromTodayLocalDate } from '../utils/dateOnly';
import { useHandsontableSuggest } from '../hooks/useHandsontableSuggest';

const CUSTOMER_ID = 'otoki';
const LINE_HEADERS = ['B/L', '품목', '수량', '포장', '중량(KG)', 'PLT'];
const MIN_LINE_ROWS = 10;

interface Destination {
  id: string;
  name: string;
  address?: string | null;
  phone_landline?: string | null;
  phone_mobile?: string | null;
  phones?: string | null;
  manager_name?: string | null;
}

interface Item {
  id: string;
  item_name: string;
}

interface InvoiceSummary {
  id: string;
  shipment_date: string;
  destination_name: string;
  vehicle_number: string;
  line_count: number;
}

interface Props {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
  embedded?: boolean;
}

const emptyLines = () => {
  const rows: any[][] = [];
  for (let i = 0; i < MIN_LINE_ROWS; i++) rows.push(['', '', '', '', '', '']);
  return rows;
};

const ProcurementInvoicePage: React.FC<Props> = ({ isFullScreen, setIsFullScreen, embedded = false }) => {
  const hotRef = useRef<any>(null);
  const itemNamesRef = useRef<string[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [itemNames, setItemNames] = useState<string[]>([]);
  const [lineData, setLineData] = useState<any[][]>(emptyLines());
  const [invoiceList, setInvoiceList] = useState<InvoiceSummary[]>([]);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [shipmentDate, setShipmentDate] = useState(() => todayLocalDate());
  const [destinationId, setDestinationId] = useState('');
  const [releaseLocation, setReleaseLocation] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [driverContact, setDriverContact] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [memo, setMemo] = useState('');
  const [listStart, setListStart] = useState(() => monthStartLocalDate());
  const [listEnd, setListEnd] = useState(() => daysFromTodayLocalDate(7));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    itemNamesRef.current = itemNames;
  }, [itemNames]);

  const getSuggestSource = useCallback((row: number, col: number) => {
    if (col === 1 && itemNamesRef.current.length > 0) {
      return { title: '품목', options: itemNamesRef.current };
    }
    return null;
  }, []);

  const {
    SuggestOverlay,
    handleAfterBeginEditing,
    handleBeforeKeyDown,
    enforceSuggestChange,
  } = useHandsontableSuggest({ hotRef, getSuggestSource });

  const handleAfterChange = useCallback((changes: any, source: string) => {
    if (!changes || source === 'loadData') return;
    if (source === 'cd-strict-reject') return;
    enforceSuggestChange(changes, source);
  }, [enforceSuggestChange]);

  const loadMasters = useCallback(async () => {
    const [dRes, iRes] = await Promise.all([
      fetch(`/api/procurement/destinations?customerId=${CUSTOMER_ID}`),
      fetch(`/api/procurement/items?customerId=${CUSTOMER_ID}`),
    ]);
    if (dRes.ok) {
      const d = await dRes.json();
      setDestinations(d);
      if (!destinationId && d.length > 0) setDestinationId(d[0].id);
    }
    if (iRes.ok) {
      const list = await iRes.json();
      setItems(list);
      setItemNames(list.map((x: Item) => x.item_name));
    }
  }, [destinationId]);

  const loadInvoiceList = useCallback(async () => {
    const params = new URLSearchParams({
      customerId: CUSTOMER_ID,
      start: listStart,
      end: listEnd,
    });
    const res = await fetch(`/api/procurement/invoices?${params}`);
    if (res.ok) setInvoiceList(await res.json());
  }, [listStart, listEnd]);

  useEffect(() => { loadMasters(); }, [loadMasters]);
  useEffect(() => { loadInvoiceList(); }, [loadInvoiceList]);

  const resetForm = () => {
    setInvoiceId(null);
    setShipmentDate(todayLocalDate());
    setReleaseLocation('');
    setVehicleNumber('');
    setDriverContact('');
    setArrivalDate('');
    setMemo('');
    setLineData(emptyLines());
  };

  const loadInvoice = async (id: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/procurement/invoices/${id}?customerId=${CUSTOMER_ID}`);
      if (!res.ok) throw new Error('송장을 불러오지 못했습니다.');
      const inv = await res.json();
      setInvoiceId(inv.id);
      setShipmentDate(toDateOnlyString(inv.shipment_date));
      setDestinationId(inv.destination_id);
      setReleaseLocation(inv.release_location || '');
      setVehicleNumber(inv.vehicle_number || '');
      setDriverContact(formatPhoneKoreanInput(inv.driver_contact || ''));
      setArrivalDate(toDateOnlyString(inv.arrival_at));
      setMemo(inv.memo || '');
      const rows = (inv.lines || []).map((l: any) => [
        l.line_memo || '',
        l.item_name,
        l.box_qty ?? '',
        l.pack_unit ?? '',
        l.weight_kg ?? '',
        l.pallet_qty ?? '',
      ]);
      while (rows.length < MIN_LINE_ROWS) rows.push(['', '', '', '', '', '']);
      setLineData(rows);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const resolveItemId = (name: string): string | null => {
    const n = name.trim();
    const found = items.find(i => i.item_name === n);
    return found?.id ?? null;
  };

  const collectLinesFromForm = () => {
    const hot = hotRef.current?.hotInstance;
    const data: any[][] = hot ? hot.getData() : lineData;
    const lines: Array<{ itemId: string; boxQty: string | null; packUnit: string | null; weightKg: number | null; palletQty: number | null; lineMemo: string | null; itemName: string }> = [];

    for (const row of data) {
      const lineMemo = String(row[0] || '').trim() || null;
      const itemName = String(row[1] || '').trim();
      if (!itemName) continue;
      const itemId = resolveItemId(itemName);
      if (!itemId) {
        return { error: `등록되지 않은 품목입니다. 품목을 먼저 등록하세요: ${itemName}` as const, lines: [] };
      }
      const qtyRaw = row[2] !== '' && row[2] != null ? String(row[2]).trim() : '';
      if (qtyRaw && !/^\d+(\.\d+)?$/.test(qtyRaw.replace(/,/g, ''))) {
        return { error: `수량은 숫자만 입력하세요. (품목: ${itemName})` as const, lines: [] };
      }
      lines.push({
        itemId,
        itemName,
        lineMemo,
        boxQty: qtyRaw ? qtyRaw.replace(/,/g, '') : null,
        packUnit: row[3] !== '' && row[3] != null ? String(row[3]).trim() || null : null,
        weightKg: row[4] !== '' && row[4] != null ? Number(row[4]) : null,
        palletQty: row[5] !== '' && row[5] != null ? Number(row[5]) : null,
      });
    }
    if (lines.length === 0) {
      return { error: '품목 행을 1개 이상 입력하세요.' as const, lines: [] };
    }
    return { error: null, lines };
  };

  const buildPdfData = (): ProcurementInvoicePdfData | null => {
    if (!shipmentDate) {
      alert('출고일을 입력하세요.');
      return null;
    }
    const dest = destinations.find(d => d.id === destinationId);
    if (!dest) {
      alert('입고처(공장)를 선택하세요.');
      return null;
    }
    const collected = collectLinesFromForm();
    if (collected.error) {
      alert(collected.error);
      return null;
    }
    return {
      shipmentDate,
      destinationName: dest.name,
      destinationAddress: dest.address || '',
      destinationPhoneLandline: dest.phone_landline || '',
      destinationPhoneMobile: dest.phone_mobile || '',
      destinationPhones: dest.phones || '',
      destinationManager: dest.manager_name || '',
      releaseLocation: releaseLocation.trim(),
      vehicleNumber: vehicleNumber.trim(),
      driverContact: driverContact.trim(),
      arrivalAt: arrivalDate || null,
      memo: memo.trim(),
      lines: collected.lines.map(l => ({
        lineMemo: l.lineMemo,
        itemName: l.itemName,
        boxQty: l.boxQty,
        packUnit: l.packUnit,
        weightKg: l.weightKg,
        palletQty: l.palletQty,
        releaseLocation: '',
      })),
    };
  };

  const handlePdfDownload = async () => {
    const pdfData = buildPdfData();
    if (!pdfData) return;

    if (!invoiceId) {
      const ok = window.confirm('저장되지 않은 송장입니다. 현재 화면 내용으로 PDF를 출력할까요?\n(저장 후 출력을 권장합니다.)');
      if (!ok) return;
    }

    setPdfGenerating(true);
    try {
      const filename = buildProcurementInvoiceFilename(
        pdfData.shipmentDate,
        pdfData.destinationName,
        pdfData.vehicleNumber
      );
      await downloadProcurementInvoicePdf(pdfData, filename);
    } catch (e) {
      console.error('송장 PDF 생성 오류:', e);
      alert('송장 PDF 생성 중 오류가 발생했습니다.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleSave = async () => {
    const collected = collectLinesFromForm();
    if (collected.error) {
      alert(collected.error);
      return;
    }
    const lines = collected.lines;

    if (!shipmentDate) {
      alert('출고일을 입력하세요.');
      return;
    }
    if (!destinationId) {
      alert('입고처(공장)를 선택하세요.');
      return;
    }
    if (lines.length === 0) {
      alert('품목 행을 1개 이상 입력하세요.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customerId: CUSTOMER_ID,
        shipmentDate,
        destinationId,
        releaseLocation: releaseLocation || null,
        vehicleNumber: vehicleNumber || null,
        driverContact: driverContact || null,
        arrivalAt: arrivalDate || null,
        memo: memo || null,
        createdBy: auth.currentUser?.email || null,
        updatedBy: auth.currentUser?.email || null,
        lines: lines.map(l => ({
          itemId: l.itemId,
          boxQty: l.boxQty,
          packUnit: l.packUnit,
          weightKg: l.weightKg,
          palletQty: l.palletQty,
          lineMemo: l.lineMemo,
        })),
      };

      const url = invoiceId
        ? `/api/procurement/invoices/${invoiceId}`
        : '/api/procurement/invoices';
      const res = await fetch(url, {
        method: invoiceId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || '저장 실패');
      setInvoiceId(body.id);
      alert('송장이 저장되었습니다.');
      await loadInvoiceList();
    } catch (e: any) {
      alert(e.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!invoiceId) return;
    if (!window.confirm('이 송장을 삭제하시겠습니까?')) return;
    const res = await fetch(`/api/procurement/invoices/${invoiceId}?customerId=${CUSTOMER_ID}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = await res.json();
      alert(body.error || '삭제 실패');
      return;
    }
    resetForm();
    await loadInvoiceList();
  };

  const fieldStyle: React.CSSProperties = { padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 14 };

  return (
    <div style={{
      padding: embedded ? 0 : '12px 16px',
      minHeight: embedded ? undefined : 'calc(100vh - 48px)',
      height: embedded ? '100%' : undefined,
      overflow: embedded ? 'auto' : undefined,
      boxSizing: 'border-box',
    }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: '#1976d2', fontSize: 22 }}>조달실 — 송장 입력 (OLS)</h2>
          <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 520px', minWidth: 320 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 16, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>
              출고일
              <input type="date" value={shipmentDate} onChange={e => setShipmentDate(e.target.value)} style={fieldStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>
              입고처(공장)
              <select value={destinationId} onChange={e => setDestinationId(e.target.value)} style={fieldStyle}>
                <option value="">선택</option>
                {destinations.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>
              출고장소
              <input value={releaseLocation} onChange={e => setReleaseLocation(e.target.value)} placeholder="예: 삼일냉동" style={fieldStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>
              차량번호
              <input value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)} style={fieldStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>
              기사 연락처
              <input
                value={driverContact}
                onChange={e => setDriverContact(formatPhoneKoreanInput(e.target.value))}
                inputMode="tel"
                style={fieldStyle}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600 }}>
              도착일
              <input type="date" value={arrivalDate} onChange={e => setArrivalDate(e.target.value)} style={fieldStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, gridColumn: '1 / -1' }}>
              비고 (냉동 온도 등)
              <input value={memo} onChange={e => setMemo(e.target.value)} style={fieldStyle} />
            </label>
          </div>

          <p style={{ fontSize: 14, color: '#666', margin: '0 0 8px' }}>품목 — 등록된 품목만 선택 가능 (자동완성)</p>
          {loading ? (
            <div style={{ padding: 24, color: '#888' }}>불러오는 중…</div>
          ) : (
            <HotTable
              ref={hotRef}
              data={lineData}
              colHeaders={LINE_HEADERS}
              rowHeaders
              width="100%"
              height={560}
              licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
              stretchH="all"
              columns={[
                { type: 'text' },
                { type: 'text' },
                { type: 'numeric', numericFormat: { pattern: '#,##0' } },
                { type: 'text' },
                { type: 'numeric' },
                { type: 'numeric' },
              ]}
              afterChange={handleAfterChange}
              afterBeginEditing={handleAfterBeginEditing}
              beforeKeyDown={handleBeforeKeyDown}
            />
          )}

          {SuggestOverlay}

          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setLineData(prev => [...prev, ['', '', '', '', '', '']])} style={{ padding: '8px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>+ 품목 행</button>
            <button type="button" onClick={handleSave} disabled={saving} style={{ padding: '8px 14px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>{saving ? '저장 중…' : invoiceId ? '수정 저장' : '송장 저장'}</button>
            <button type="button" onClick={resetForm} style={{ padding: '8px 14px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>새 송장</button>
            {invoiceId && (
              <button type="button" onClick={handleDelete} style={{ padding: '8px 14px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>삭제</button>
            )}
            <button
              type="button"
              onClick={handlePdfDownload}
              disabled={pdfGenerating || saving}
              style={{
                padding: '8px 14px',
                background: '#1565c0',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: pdfGenerating ? 'wait' : 'pointer',
                fontWeight: 600,
              }}
            >
              {pdfGenerating ? 'PDF 생성 중…' : '송장 PDF'}
            </button>
          </div>
        </div>

        <div style={{ flex: '0 0 300px', maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fff' }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: '#334155' }}>송장 목록</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <input type="date" value={listStart} onChange={e => setListStart(e.target.value)} style={{ ...fieldStyle, flex: 1, minWidth: 120 }} />
            <input type="date" value={listEnd} onChange={e => setListEnd(e.target.value)} style={{ ...fieldStyle, flex: 1, minWidth: 120 }} />
            <button type="button" onClick={loadInvoiceList} style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer' }}>조회</button>
          </div>
          <div style={{ maxHeight: 640, overflow: 'auto' }}>
            {invoiceList.length === 0 ? (
              <div style={{ color: '#888', fontSize: 13, padding: 8 }}>기간 내 송장 없음</div>
            ) : (
              invoiceList.map(inv => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => loadInvoice(inv.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    marginBottom: 6,
                    border: invoiceId === inv.id ? '2px solid #1976d2' : '1px solid #e2e8f0',
                    borderRadius: 6,
                    background: invoiceId === inv.id ? '#e3f2fd' : '#fafafa',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{toDateOnlyString(inv.shipment_date)}</div>
                  <div>{inv.destination_name} · {inv.line_count}품목</div>
                  {inv.vehicle_number && <div style={{ color: '#64748b' }}>{inv.vehicle_number}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcurementInvoicePage;
